import { useState } from "react";

import { ITenant } from "../../../@types/admin/AdminHospital";
import {
  useDeleteTenantLogo,
  useUploadTenantLogo,
} from "../../../hooks/mutations/admin/hospitals";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FileUploadField,
  FilterButton,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  SectionActions,
  PageInfoTooltip,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionTitleGroup,
  WarningNotice,
} from "../shared";
import {
  LogoLayout,
  LogoPreview,
  LogoPreviewEmpty,
  LogoPreviewFrame,
  ModalBodyText,
} from "./AdminTenants.styles";

interface ITenantLogoCardProps {
  tenant: ITenant;
}

/** What the API accepts for a tenant logo, mirroring the portal's own loader. */
const ACCEPTED_TYPES = [".svg", ".png", "image/svg+xml", "image/png"];
const MAX_LOGO_BYTES = 1024 * 1024;

interface IDeleteLogoModalProps {
  tenant: ITenant;
  onClose: () => void;
}

function DeleteLogoModal({ tenant, onClose }: IDeleteLogoModalProps) {
  const { showToast } = useToast();
  const { deleteTenantLogo, isDeletingLogo } = useDeleteTenantLogo();

  const handleDelete = async () => {
    try {
      await deleteTenantLogo(tenant.id);
      showToast("Successfully deleted tenant logo", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error deleting the tenant logo."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Delete logo</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Remove <strong>{tenant.name}</strong>&apos;s logo?
        </ModalBodyText>
        <WarningNotice>
          Everyone in this tenant falls back to the default patientMpower logo
          immediately.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isDeletingLogo}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isDeletingLogo}
            onClick={handleDelete}
          >
            {isDeletingLogo ? "Deleting..." : "Delete logo"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}

/**
 * Tenant logo upload and delete (EN-5154) — Retool's `logoDropzone` +
 * `EditLogo` / `DeleteLogo`. First page in the epic to use the shared upload
 * helpers from EN-5148.
 *
 * The preview is loaded straight from the logo bucket, the same URL the portal
 * chrome uses (`TopPanelMenu`), with a cache-busting stamp so a replaced logo
 * doesn't keep showing the old file. The stamp is the API's `logoVersion`
 * (the file's S3 ETag), which moves on every upload; `tenant.updated` is the
 * DB row's timestamp and stays put when only the logo changes, so a reload
 * used to rebuild the exact pre-upload URL and get the cached old image back.
 */
export function TenantLogoCard({ tenant }: ITenantLogoCardProps) {
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const { uploadTenantLogo, isUploadingLogo } = useUploadTenantLogo();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  // Set on a successful upload so the preview refreshes at once, before the
  // invalidated tenant query comes back with the new `logoVersion`.
  const [uploadStamp, setUploadStamp] = useState("");

  const previewStamp = [tenant.logoVersion, uploadStamp]
    .filter(Boolean)
    .join("-");

  const logoUrl =
    tenant.hasLogo && process.env.REACT_APP_TENANT_LOGO_BASE_URL
      ? `${process.env.REACT_APP_TENANT_LOGO_BASE_URL}${tenant.id}.${
          tenant.logoExtension || "svg"
        }?v=${encodeURIComponent(previewStamp)}`
      : "";

  const handleUpload = async () => {
    if (!logoFile) return;

    try {
      await uploadTenantLogo({ tenantId: tenant.id, image: logoFile });
      showToast("Successfully updated tenant logo", "success");
      setLogoFile(null);
      setUploadStamp(Date.now().toString(36));
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating the tenant logo."
        ),
        "warning"
      );
    }
  };

  const handleDeleteClick = () => {
    openModal(<DeleteLogoModal tenant={tenant} onClose={closeModal} />, {
      width: "440px",
      showCloseButton: true,
    });
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitleGroup>
          <SectionTitle>Logo</SectionTitle>
          <PageInfoTooltip title="Logo">
            Shown in the portal header to everyone in this tenant.
          </PageInfoTooltip>
        </SectionTitleGroup>
      </SectionHeader>

      <LogoLayout>
        <LogoPreviewFrame>
          {logoUrl ? (
            <LogoPreview src={logoUrl} alt={`${tenant.name} logo`} />
          ) : (
            <LogoPreviewEmpty>
              No logo — this tenant shows the default patientMpower one.
            </LogoPreviewEmpty>
          )}
        </LogoPreviewFrame>

        {/* The guidance goes through `helperText`, which prints it on one
            line with the accepted types and the size cap. It used to be a
            second FieldHint under the field, so the card carried two hint
            paragraphs about the same thing at two different indents. */}
        <FileUploadField
          label={tenant.hasLogo ? "Replace logo" : "Upload a logo"}
          accept={ACCEPTED_TYPES}
          maxBytes={MAX_LOGO_BYTES}
          helperText="SVG keeps its edges at every size; PNG is scaled by the browser."
          value={logoFile}
          onChange={setLogoFile}
          disabled={isUploadingLogo}
        />
      </LogoLayout>

      <SectionActions>
        {tenant.hasLogo && (
          <FilterButton
            variant="secondary"
            disabled={isUploadingLogo}
            onClick={handleDeleteClick}
          >
            Delete logo
          </FilterButton>
        )}
        <FilterButton
          variant="primary"
          disabled={!logoFile || isUploadingLogo}
          onClick={handleUpload}
        >
          {isUploadingLogo ? "Uploading..." : "Upload logo"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
